import { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import ModernSidebar from '../components/sideBar/sideBar'
import Navbar from '../components/navbar/navbar'
import localStorage from 'local-storage'
import { Box, Container, useTheme, alpha, Fade } from '@mui/material'
import { motion } from 'framer-motion'

const ModernMainLayout = () => {
  const [isOpen, setIsOpen] = useState(true)
  const [mounted, setMounted] = useState(false)
  const theme = useTheme()

  useEffect(() => {
    const sideBarOpen = localStorage.get('sidebar')

    if (sideBarOpen === 'false') {
      setIsOpen(false)
    } else {
      setIsOpen(true)
    }

    // Mount animation için
    setTimeout(() => setMounted(true), 100)
  }, [])

  const toggleSidebar = () => {
    setIsOpen(!isOpen)
  }

  // Sidebar genişlikleri — Sidebar.dc.html tasarımıyla aynı (260 / 76)
  const sidebarWidth = {
    closed: 76,
    open: 260,
  }

  const currentSidebarWidth = isOpen ? sidebarWidth.open : sidebarWidth.closed

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        width: '100%',
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(
          theme.palette.background.paper,
          0.8
        )} 100%)`,
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Effects */}
      <Box
        component={motion.div}
        animate={{
          opacity: [0.3, 0.5, 0.3],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        sx={{
          position: 'fixed',
          top: '-10%',
          left: '-5%',
          width: '40%',
          height: '40%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          pointerEvents: 'none',
          filter: 'blur(60px)',
          zIndex: 0,
        }}
      />

      <Box
        component={motion.div}
        animate={{
          opacity: [0.2, 0.4, 0.2],
          scale: [1.1, 1, 1.1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
        sx={{
          position: 'fixed',
          bottom: '-10%',
          right: '-5%',
          width: '50%',
          height: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.06)} 0%, transparent 70%)`,
          pointerEvents: 'none',
          filter: 'blur(80px)',
          zIndex: 0,
        }}
      />

      {/* Grid Pattern Overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
          zIndex: 0,
          opacity: 0.5,
        }}
      />

      {/* Sidebar */}
      <Box
        sx={{
          width: currentSidebarWidth,
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: 1200,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <ModernSidebar status={isOpen} toggleSidebar={toggleSidebar} />
      </Box>

      {/* Main Content Area */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: mounted ? 1 : 0, x: mounted ? 0 : 20 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        sx={{
          marginLeft: `${currentSidebarWidth}px`,
          width: `calc(100% - ${currentSidebarWidth}px)`,
          display: 'flex',
          flexDirection: 'column',
          transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          minHeight: '100vh',
        }}
      >
        {/* Navbar - isteğe bağlı */}
        {/* <Box
          sx={{
            width: '100%',
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            backdropFilter: 'blur(20px)',
            background: alpha(theme.palette.background.paper, 0.8),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.primary.main, 0.05)}`,
          }}
        >
          <Navbar />
        </Box> */}

        {/* Content Container */}
        <Container
          maxWidth="xl"
          sx={{
            flex: 1,
            py: 4,
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Fade in={mounted} timeout={800}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              sx={{
                minHeight: 'calc(100vh - 120px)',
                position: 'relative',
              }}
            >
              {/* Content */}
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Outlet />
              </Box>
            </Box>
          </Fade>
        </Container>

        {/* Footer gradient effect */}
        <Box
          sx={{
            width: '100%',
            height: 60,
            background: `linear-gradient(to top, ${alpha(theme.palette.primary.main, 0.05)}, transparent)`,
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Floating particles effect - optional */}
      {[...Array(3)].map((_, index) => (
        <Box
          key={index}
          component={motion.div}
          animate={{
            y: [0, -30, 0],
            x: [0, 15, 0],
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{
            duration: 8 + index * 2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: index * 2,
          }}
          sx={{
            position: 'fixed',
            width: 4,
            height: 4,
            borderRadius: '50%',
            background: theme.palette.primary.main,
            boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.6)}`,
            left: `${20 + index * 30}%`,
            top: `${30 + index * 20}%`,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ))}

      {/* Ambient light effect */}
      <Box
        component={motion.div}
        animate={{
          opacity: [0.05, 0.15, 0.05],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '150%',
          height: '150%',
          background: `radial-gradient(ellipse at center, ${alpha(
            theme.palette.primary.main,
            0.03
          )} 0%, transparent 60%)`,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Top glow line */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: currentSidebarWidth,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, ${alpha(
            theme.palette.primary.main,
            0.5
          )}, transparent)`,
          zIndex: 1100,
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </Box>
  )
}

export default ModernMainLayout